import { Avatar, Box, Button, Chip, CircularProgress, Dialog, Divider, FormControl, IconButton, MenuItem, Select, Typography } from "@mui/material";
import { ACCENT, TEMPLATE_AVATAR_GRAD } from "./constants";
import { formatPrice, getCategoryAvatar } from "./helpers";
import TemplatePreviewCanvas from "../../Components/TemplatePreviewCanvas";
import callPiggybackAPI from "../../Helpers/piggyback.api.helper";
import { useSelector } from "react-redux";
import { useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import ExtensionIcon from "@mui/icons-material/Extension";
import GridViewIcon from "@mui/icons-material/GridView";
import ShoppingBagOutlinedIcon from "@mui/icons-material/ShoppingBagOutlined";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import { useTheme } from "@mui/material/styles";






const BundleSidebar = ({
  ACCENT,
  bundleItems,
  previewId,
  setPreviewId,
  onClose,
  selectedSite,
  setSelectedSite,
  selectedSiteObj,
  sites,
  toggleBundle,
  formatPrice,
  ItemAvatar,
  style={}
}) => {
  return (
    <Box
      sx={{
        width: { xs: "100%", md: "38%" },
        flexShrink: 0,
        borderRight: { xs: "none", md: "1px solid #f0f0f0" },
        display: "flex",
        flexDirection: "column",
        ...style
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: { xs: 2, md: 2.5 },
          py: { xs: 1.5, md: 2 },
          borderBottom: "1px solid #f5f5f5",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box>
          <Typography
            variant="subtitle1"
            fontWeight={800}
            sx={{ color: "#1a1a1a", lineHeight: 1.2, fontSize: { xs: "0.95rem", md: "unset" } ,fontWeight:{xs:"unset",md:800} }}
          >
            Your Bundle
          </Typography>

          <Typography variant="caption" color="text.secondary">
            {bundleItems.length} item{bundleItems.length !== 1 ? "s" : ""}
          </Typography>
        </Box>

        <IconButton
          size="small"
          onClick={onClose}
          sx={{
            color: "#aaa",
            "&:hover": { color: "#555" },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Site Selector */}
      <Box
        sx={{
          px: { xs: 2, md: 2.5 },
          py: { xs: 1.4, md: 1.8 },
          borderBottom: "1px solid #f5f5f5",
        }}
      >
        <Typography
          sx={{
            fontSize: "0.63rem",
            fontWeight: 700,
            letterSpacing: 1.3,
            color: "#bbb",
            textTransform: "uppercase",
            mb: 0.8,
          }}
        >
          Deploy to Site
        </Typography>

        <FormControl size="small" fullWidth>
          <Select
            value={selectedSite}
            onChange={(e) => setSelectedSite(e.target.value)}
            displayEmpty
            sx={{
              fontSize: "0.82rem",
              borderRadius: 1.5,
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: selectedSite ? ACCENT : undefined,
              },
            }}
            renderValue={(val) => {
              if (!val) {
                return (
                  <Typography
                    sx={{
                      fontSize: "0.82rem",
                      color: "#aaa",
                    }}
                  >
                    Select a site
                  </Typography>
                );
              }

              return selectedSiteObj?.name ?? val;
            }}
          >
            {sites.length === 0 && (
              <MenuItem
                disabled
                sx={{
                  fontSize: "0.82rem",
                  color: "#aaa",
                }}
              >
                No sites available
              </MenuItem>
            )}

            {sites.map((s) => {
              const id = s.id ?? s._id;

              return (
                <MenuItem
                  key={id}
                  value={id}
                  sx={{ fontSize: "0.82rem" }}
                >
                  <Box>
                    <Typography
                      sx={{
                        fontSize: "0.82rem",
                        fontWeight: 600,
                        lineHeight: 1.3,
                      }}
                    >
                      {s.name}
                    </Typography>

                    {s.location && (
                      <Typography
                        sx={{
                          fontSize: "0.7rem",
                          color: "#999",
                        }}
                      >
                        {s.location}
                      </Typography>
                    )}
                  </Box>
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>

        {sites.length > 0 && !selectedSite && (
          <Typography
            sx={{
              fontSize: "0.62rem",
              color: "#f59e0b",
              mt: 0.6,
            }}
          >
            Please select a site to continue
          </Typography>
        )}
      </Box>

      {/* Bundle Items */}
      <Box sx={{ flex: 1, overflowY: "auto", WebkitOverflowScrolling: "touch", scrollbarWidth: "thin", scrollbarColor: "transparent transparent", "&::-webkit-scrollbar": { width: 2 }, "&::-webkit-scrollbar-track": { background: "transparent" }, "&::-webkit-scrollbar-thumb": { background: "transparent", borderRadius: 4, transition: "background 0.2s" }, "&:hover::-webkit-scrollbar-thumb": { background: "rgba(0,0,0,0.2)" } }}>
        {bundleItems.length === 0 ? (
          <Box
            sx={{
              p: 4,
              textAlign: "center",
            }}
          >
            <ShoppingCartIcon
              sx={{
                fontSize: 36,
                color: "#e0e0e0",
                mb: 1,
              }}
            />

            <Typography variant="body2" color="text.secondary">
              Your bundle is empty
            </Typography>
          </Box>
        ) : (
          bundleItems.map((item) => {
            const isSelected = item.id === previewId;

            return (
              <Box
                key={item.id}
                onClick={() => setPreviewId(item.id)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: { xs: 1, md: 1.5 },
                  px: { xs: 1.5, md: 2 },
                  py: 1.4,
                  cursor: "pointer",
                  borderLeft: `3px solid ${
                    isSelected ? ACCENT : "transparent"
                  }`,
                  backgroundColor: isSelected ? "#fdf2f4" : "transparent",
                  "&:hover": {
                    backgroundColor: isSelected
                      ? "#fdf2f4"
                      : "#fafafa",
                  },
                  transition: "background-color 0.15s",
                }}
              >
                <ItemAvatar item={item} />

                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    sx={{
                      color: "#1a1a1a",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      lineHeight: 1.3,
                    }}
                  >
                    {item.name}
                  </Typography>

                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      mt: 0.2,
                    }}
                  >
                    <Chip
                      label={item.type}
                      size="small"
                      sx={{
                        height: 16,
                        fontSize: "0.58rem",
                        px: 0,
                      }}
                    />

                    <Typography
                      variant="caption"
                      sx={{
                        color:
                          item.price === 0 ? "#2e7d32" : "#888",
                        fontWeight:
                          item.price === 0 ? 600 : 400,
                      }}
                    >
                      {formatPrice(item.price)}
                    </Typography>
                  </Box>
                </Box>

                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleBundle(item.id);

                    if (item.id === previewId) {
                      setPreviewId(
                        bundleItems.find((i) => i.id !== item.id)?.id ?? null
                      );
                    }
                  }}
                  sx={{
                    p: 0.3,
                    color: "#ccc",
                    flexShrink: 0,
                    "&:hover": {
                      color: ACCENT,
                    },
                  }}
                >
                  <CloseIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Box>
            );
          })
        )}
      </Box>
    </Box>
  );
};



function BundleModal({ bundle, items, purchased, handleGet, toggleBundle, onClose, clearBundle, onCheckoutSuccess }) {

  const bundleItems = bundle.map((id) => items.find((i) => i.id === id)).filter(Boolean);
  const [previewId, setPreviewId] = useState(bundleItems[0]?.id ?? null);
  const [selectedSite, setSelectedSite] = useState("");
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [checkoutError, setCheckoutError]     = useState(null);
  const sites = useSelector((state) => state.truInk?.allSites ?? []);
  const previewItem = bundleItems.find((i) => i.id === previewId) ?? bundleItems[0] ?? null;
  const total = bundleItems.reduce((sum, i) => sum + i.price, 0);
  const selectedSiteObj = sites.find((s) => (s.id ?? s._id) === selectedSite) ?? null;
  const canCheckout = bundleItems.length > 0 && (sites.length === 0 || !!selectedSite);
  const theme = useTheme();


  const handleCheckout = async () => {
    const templates   = bundleItems.filter((i) => i.type === "Template");
    const plugins     = bundleItems.filter((i) => i.type === "Plugin");
    const templateIds = templates.map((i) => i.id);
    const siteId      = selectedSite;
    const tenantId    = selectedSiteObj?.tenantId;

    setCheckoutLoading(true);
    setCheckoutError(null);
    try {
      try {
        await Promise.all([
          templateIds.length > 0 && callPiggybackAPI(
            "inkHub/onBoard",
            {},
            { siteId, tenantId, type: "Template", properties: { templateList: templateIds } },
            "POST"
          ),
          ...plugins.map((plugin) =>
            callPiggybackAPI(
              "inkHub/onBoard",
              {},
              { siteId, tenantId, type: "Plugin", properties: { pluginId: plugin.id } },
              "POST"
            )
          ),
        ].filter(Boolean));
      } catch (err) {
        console.error("inkHub onboard failed:", err);
        const msg = err?.response?.data?.message || err?.message || null;
        throw new Error(msg ? `Onboarding failed: ${msg}` : "Onboarding failed. Please try again.");
      }

      // if (plugins.length > 0) {
      //   try {
      //     await Promise.all(
      //       plugins.map((plugin) =>
      //         callPiggybackAPI(
      //           "pluginSettings",
      //           {},
      //           { pluginId: plugin.id, siteId, tenantId, settings: {} },
      //           "POST"
      //         )
      //       )
      //     );
      //   } catch (err) {
      //     console.error("Plugin registration failed:", err);
      //     const msg = err?.response?.data?.message || err?.message || null;
      //     throw new Error(msg ? `Plugin registration failed: ${msg}` : "Plugins were onboarded but could not be registered to the site. Please try again.");
      //   }
      // }

      bundleItems.forEach((item) => handleGet(item));
      if (clearBundle) clearBundle();
      if (onCheckoutSuccess) onCheckoutSuccess(selectedSiteObj, bundleItems);
      onClose();
    } catch (err) {
      console.error("Checkout error:", err);
      setCheckoutError(err?.message || "Checkout failed. Please try again.");
    } finally {
      setCheckoutLoading(false);
    }
  };

  const ItemAvatar = ({ item }) => {
    const { icon: Icon } = item.type === "Plugin"
      ? { icon: ExtensionIcon }
      : getCategoryAvatar(item.category);
    return (
      <Avatar variant="rounded" sx={{ width: 36, height: 36, borderRadius: 1.5, flexShrink: 0, background: item.type === "Plugin" ? ACCENT : TEMPLATE_AVATAR_GRAD }}>
        <Icon sx={{ fontSize: 18, color: "#fff" }} />
      </Avatar>
    );
  };

  return (
  <Dialog open onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: {xs:1.5,md:3}, overflow: "hidden", height: { xs: "auto", md: 530 }, maxHeight: { xs: "95vh", md: "unset" }, margin: { xs: 1, md: "unset" }, [theme.breakpoints.down("md")]: { width: "95%" } } }}>
      <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, height: { xs: "auto", md: "100%" }, overflowY: { xs: "auto", md: "hidden" } }}>
        <BundleSidebar
        ACCENT={ACCENT}
        bundleItems={bundleItems}
        previewId={previewId}
        setPreviewId={setPreviewId}
        onClose={onClose}
        selectedSite={selectedSite}
        setSelectedSite={setSelectedSite}
        selectedSiteObj={selectedSiteObj}
        sites={sites}
        toggleBundle={toggleBundle}
        formatPrice={formatPrice}
        ItemAvatar={ItemAvatar}
        style={{display: { xs: "none", md: "flex" }}}
      />
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0, overflow: { xs: "visible", md: "hidden" } }}>
          <Box sx={{ position: "relative", backgroundColor: "#e8e8e8", overflow: "hidden", height: { xs: 190, md: 250 }, flexShrink: 0 }}>
            {previewItem ? (
              previewItem.elements ? (
                <TemplatePreviewCanvas elements={previewItem.elements} resolution={previewItem.resolution} />
              ) : previewItem.imageUrl ? (
                <Box sx={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <img src={previewItem.imageUrl} alt={previewItem.name} style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }} />
                </Box>
              ) : (
                <Box sx={{ width: "100%", height: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 1.5 }}>
                  {previewItem.type === "Plugin" ? (
                    <ExtensionIcon sx={{ fontSize: 48, color: "#ccc" }} />
                  ) : (
                    <GridViewIcon sx={{ fontSize: 48, color: "#ccc" }} />
                  )}
                  <Typography variant="caption" color="text.secondary">{previewItem.name}</Typography>
                </Box>
              )
            ) : (
              <Box sx={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Typography variant="body2" color="text.secondary">Select an item to preview</Typography>
              </Box>
            )}
            {previewItem && (
              <Box sx={{ position: "absolute", bottom: 0, left: 0, right: 0, background: "linear-gradient(to top, rgba(0,0,0,0.55) 0%, transparent 100%)", px: 2, py: 1.5 }}>
                <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: "0.82rem" }}>{previewItem.name}</Typography>
                {previewItem.category && (
                  <Typography sx={{ color: "rgba(255,255,255,0.7)", fontSize: "0.7rem" }}>{previewItem.category}</Typography>
                )}
              </Box>
            )}
          </Box>
          <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, mt: { xs: 0, md: "auto" }, flexShrink: 0 }}>

  <BundleSidebar
        ACCENT={ACCENT}
        bundleItems={bundleItems}
        previewId={previewId}
        setPreviewId={setPreviewId}
        onClose={onClose}
        selectedSite={selectedSite}
        setSelectedSite={setSelectedSite}
        selectedSiteObj={selectedSiteObj}
        sites={sites}
        toggleBundle={toggleBundle}
        formatPrice={formatPrice}
        ItemAvatar={ItemAvatar}
        style={{display: { xs: "flex", md: "none" }, width: "100%", borderBottom: "1px solid #f0f0f0", maxHeight: { xs: 260, md: "none" }, overflow: "hidden" }}
      />
      <Box sx={{ px: { xs: 2, md: 2.5 }, pt: 1.5, pb: 2.5, borderTop: "1px solid #f0f0f0", flexShrink: 0, width: { xs: "100%" } }}>
            <Box sx={{ mb: 1, maxHeight: 100, overflowY: "auto", WebkitOverflowScrolling: "touch", scrollbarWidth: "thin", scrollbarColor: "transparent transparent", "&::-webkit-scrollbar": { width: 2 }, "&::-webkit-scrollbar-track": { background: "transparent" }, "&::-webkit-scrollbar-thumb": { background: "transparent", borderRadius: 4, transition: "background 0.2s" }, "&:hover::-webkit-scrollbar-thumb": { background: "rgba(0,0,0,0.2)" } }}>
              {bundleItems.map((item) => (
                <Box key={item.id} sx={{ display: "flex", justifyContent: "space-between", mb: 0.4 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", mr: 1 }}>
                    {item.name}
                  </Typography>
                  <Typography variant="caption" fontWeight={600} sx={{ flexShrink: 0, color: item.price === 0 ? "#2e7d32" : "#1a1a1a" }}>
                    {formatPrice(item.price)}
                  </Typography>
                </Box>
              ))}
            </Box>
            <Divider sx={{ mb: 1.2 }} />
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", mb: 1.8 }}>
              <Typography fontWeight={700} fontSize="0.9rem" sx={{ color: "#555" }}>Total</Typography>
              <Typography fontWeight={800} sx={{ fontSize: "1.6rem", color: total === 0 ? "#2e7d32" : "#1a1a1a", lineHeight: 1 }}>
                {total === 0 ? "Free" : `₹${Number(total).toFixed(2)}`}
              </Typography>
            </Box>
            {checkoutError && (
              <Typography sx={{ fontSize: "0.72rem", color: "#d32f2f", mb: 0.8, textAlign: "center" }}>
                {checkoutError}
              </Typography>
            )}
            <Button
              fullWidth
              disableElevation
              startIcon={checkoutLoading ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : <ShoppingBagOutlinedIcon />}
              onClick={handleCheckout}
              disabled={!canCheckout || checkoutLoading}
              sx={{
                textTransform: "none",
                fontWeight: 700,
                fontSize: "0.95rem",
                py: 1.5,
                borderRadius: 2,
                background: (!canCheckout || checkoutLoading)
                  ? undefined
                  : total === 0
                    ? "linear-gradient(90deg, #388e3c, #2e7d32)"
                    : TEMPLATE_AVATAR_GRAD,
                color: "#fff",
                boxShadow: "none",
                "&:hover": { opacity: 0.9, boxShadow: "none" },
                "&.Mui-disabled": { color: "#fff", background: "#ccc" },
              }}
            >
              {checkoutLoading
                ? "Processing…"
                : bundleItems.length === 0
                  ? "Bundle is empty"
                  : sites.length > 0 && !selectedSite
                    ? "Select a site first"
                    : total === 0
                      ? `Get All Free${selectedSiteObj ? ` → ${selectedSiteObj.name}` : ""}`
                      : `Checkout · ₹${Number(total).toFixed(2)}${selectedSiteObj ? ` → ${selectedSiteObj.name}` : ""}`}
            </Button>
          </Box>

        </Box>
      </Box>
    </Box>
    </Dialog>
  );
}

export default BundleModal;